import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Empty,
  Input,
  List,
  message,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ClockCircleOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  PaperClipOutlined,
  SearchOutlined,
  SendOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import io from 'socket.io-client';
import apiClient, { API_ORIGIN } from '../api';
import {
  buildContactProfile,
  filterContacts,
  getContactInitials,
  normalizeChatId,
} from './contactConsole.helpers';
import './HumanAgentTerminal.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const SOCKET_URL = API_ORIGIN;

const queueFilters = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'requesting', label: 'Requesting' },
  { key: 'intervened', label: 'Intervened' },
];

function getBubbleType(messageItem) {
  if (messageItem.sender === 'CUSTOMER') return 'inbound';
  if (messageItem.sender === 'SYSTEM') return 'system';
  return 'outbound';
}

function formatActivity(dateValue) {
  if (!dateValue) return '-';
  return new Date(dateValue).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ContactProfilePanel({ profile, timer, isConnected }) {
  const details = [
    ['Status', profile.status],
    ['Last Active', formatActivity(profile.lastActivity)],
    ['Template Messages', profile.templateMessages],
    ['Session Messages', profile.sessionMessages],
    ['Unresolved Queries', profile.unresolvedQueries],
    ['Source', profile.source],
    ['First Message', profile.firstMessage],
    ['WA Conversation', profile.waConversation],
    ['MAU Status', profile.mauStatus],
    ['Incoming', profile.incoming],
  ];

  return (
    <aside className="hac-profile">
      <div className="hac-profile-head">Chat Profile</div>
      <div className="hac-profile-identity">
        <Avatar className="hac-profile-avatar" size={72}>
          {getContactInitials(profile.name)}
        </Avatar>
        <Title level={4}>{profile.name}</Title>
        <Text>{profile.displayPhone}</Text>
      </div>

      <Divider />

      <div className="hac-profile-card">
        {details.map(([label, value]) => (
          <div className="hac-profile-row" key={label}>
            <Text type="secondary">{label}</Text>
            <Text strong>{value}</Text>
          </div>
        ))}
        <div className="hac-profile-row">
          <Text type="secondary">Opted In</Text>
          <Switch size="small" checked={profile.optedIn} />
        </div>
      </div>

      <div className="hac-profile-card hac-runtime-card">
        <div>
          <Text type="secondary">Console state</Text>
          <Title level={5}>{isConnected ? 'Live stream connected' : 'Offline'}</Title>
        </div>
        <div className="hac-runtime-timer">
          <ClockCircleOutlined />
          <span>{timer}s</span>
        </div>
      </div>

      <div className="hac-accordion-row">Payments</div>
      <div className="hac-accordion-row">Campaigns</div>
      <div className="hac-accordion-row">Attributes</div>
      <div className="hac-accordion-row">
        Tags
        <Space size={4} wrap>
          {profile.tags.slice(0, 2).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Space>
      </div>
    </aside>
  );
}

const HumanAgentTerminal = () => {
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [queue, setQueue] = useState('all');
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [targetPhone, setTargetPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [timer, setTimer] = useState(60);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const selectedContact = useMemo(
    () => contacts.find((contact) => normalizeChatId(contact.chatId || contact.phone) === targetPhone),
    [contacts, targetPhone]
  );
  const selectedProfile = useMemo(
    () => buildContactProfile(selectedContact || { phone: targetPhone, status: 'inactive' }),
    [selectedContact, targetPhone]
  );

  const visibleContacts = useMemo(() => {
    const status = queue === 'all' ? 'all' : queue;
    return filterContacts(contacts, { search: searchQuery, status, source: 'all', tag: 'all' });
  }, [contacts, queue, searchQuery]);

  const counts = useMemo(() => {
    const profiles = contacts.map(buildContactProfile);
    return {
      all: profiles.length,
      active: profiles.filter((profile) => profile.status.toLowerCase() === 'active').length,
      requesting: profiles.filter((profile) => profile.status.toLowerCase() === 'requesting').length,
      intervened: profiles.filter((profile) => profile.status.toLowerCase() === 'intervened').length,
    };
  }, [contacts]);

  useEffect(() => {
    let isMounted = true;

    const fetchContacts = async () => {
      try {
        setLoadingContacts(true);
        const res = await apiClient.get('/chat-history/all');
        if (isMounted && res.data.success) {
          const chats = res.data.chats || [];
          setContacts(chats);
          setTargetPhone((currentPhone) => currentPhone || normalizeChatId(chats[0]?.chatId || chats[0]?.phone));
        }
      } catch (err) {
        console.error('Failed to fetch contacts', err);
        message.error('Failed to load contacts');
      } finally {
        if (isMounted) setLoadingContacts(false);
      }
    };

    fetchContacts();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!targetPhone) return;

    const fetchHistory = async () => {
      try {
        setLoadingMessages(true);
        const res = await apiClient.get(`/chat-history/${targetPhone}?limit=100`);
        if (res.data.success) {
          const fetchedMessages = (res.data.messages || []).map((item, index) => ({
            id: item._id || index,
            sender: item.role === 'user' ? 'CUSTOMER' : item.agentId === 'human_agent' || !item.agentId ? 'YOU' : 'AI',
            text: item.content,
            time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          }));
          setMessages(fetchedMessages.reverse());
        }
      } catch (err) {
        console.error('Failed to fetch messages', err);
        message.error('Failed to load chat history');
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchHistory();
  }, [targetPhone]);

  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current.on('connect', () => setIsConnected(true));
    socketRef.current.on('disconnect', () => setIsConnected(false));

    socketRef.current.on('chat_log', (data) => {
      const involvedPhone = normalizeChatId(data.details?.match(/\d+/)?.[0] || data.message?.match(/\d+/)?.[0]);
      if (involvedPhone) updateContactLastMessage(involvedPhone, data.details || data.message);

      setTargetPhone((currentPhone) => {
        if (currentPhone && (data.message?.includes(currentPhone) || data.details?.includes(currentPhone))) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              sender: data.type === 'response' ? 'AI' : 'SYSTEM',
              text: data.details || data.message,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            },
          ]);
        }
        return currentPhone;
      });
    });

    socketRef.current.on('message_received', (data) => {
      const fromPhone = normalizeChatId(data.from);
      updateContactLastMessage(fromPhone, data.body);

      setTargetPhone((currentPhone) => {
        if (currentPhone && normalizeChatId(data.from).includes(currentPhone)) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              sender: 'CUSTOMER',
              text: data.body,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            },
          ]);
          setLastActivity(Date.now());
        }
        return currentPhone;
      });
    });

    return () => socketRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(Math.max(0, 60 - Math.floor((Date.now() - lastActivity) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastActivity]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const updateContactLastMessage = (chatId, lastMsgText) => {
    setContacts((prev) => {
      const normalized = normalizeChatId(chatId);
      const existingIdx = prev.findIndex((contact) => normalizeChatId(contact.chatId || contact.phone).includes(normalized));
      const newContacts = [...prev];

      if (existingIdx >= 0) {
        newContacts[existingIdx] = {
          ...newContacts[existingIdx],
          lastMessage: lastMsgText,
          lastActivity: new Date().toISOString(),
          status: newContacts[existingIdx].status || 'active',
        };
        const [moved] = newContacts.splice(existingIdx, 1);
        newContacts.unshift(moved);
      } else {
        newContacts.unshift({
          chatId: normalized,
          lastMessage: lastMsgText,
          lastActivity: new Date().toISOString(),
          messageCount: 1,
          status: 'active',
        });
      }
      return newContacts;
    });
  };

  const handleSendHumanMessage = async () => {
    if (!inputText.trim() || !targetPhone) return;
    const outgoingText = inputText.trim();
    setLoading(true);

    try {
      const response = await apiClient.post('/whatsapp/message', {
        number: targetPhone,
        message: outgoingText,
      });

      if (response.data.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            sender: 'YOU',
            text: outgoingText,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        updateContactLastMessage(targetPhone, outgoingText);
        setInputText('');
        setLastActivity(Date.now());
        message.success('Human reply sent');
      }
    } catch (err) {
      message.error('Failed to send human reply');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hac-shell">
      <section className="hac-roster">
        <div className="hac-roster-header">
          <div>
            <Text type="secondary">Live chat</Text>
            <Title level={3}>Human Agent Console</Title>
          </div>
          <Badge status={isConnected ? 'success' : 'error'} text={isConnected ? 'Connected' : 'Offline'} />
        </div>

        <Input
          className="hac-search"
          prefix={<SearchOutlined />}
          suffix={<FilterOutlined />}
          placeholder="Search name or mobile number"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          allowClear
        />

        <div className="hac-queue-tabs">
          {queueFilters.map((item) => (
            <button
              className={queue === item.key ? 'is-active' : ''}
              key={item.key}
              type="button"
              onClick={() => setQueue(item.key)}
            >
              {item.label} ({counts[item.key] || 0})
            </button>
          ))}
        </div>

        <div className="hac-contact-list">
          {loadingContacts ? (
            <div className="hac-loading"><Spin /></div>
          ) : (
            <List
              dataSource={visibleContacts}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chats match this filter" /> }}
              renderItem={(item) => {
                const profile = buildContactProfile(item);
                const isSelected = targetPhone === profile.phone;

                return (
                  <button
                    type="button"
                    className={`hac-contact-row ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setTargetPhone(profile.phone)}
                  >
                    <Avatar className="hac-contact-avatar">{getContactInitials(profile.name)}</Avatar>
                    <span className="hac-contact-main">
                      <span className="hac-contact-top">
                        <Text strong>{profile.name}</Text>
                        <Text type="secondary">{formatActivity(profile.lastActivity)}</Text>
                      </span>
                      <span className="hac-contact-preview">{item.lastMessage || 'No recent messages'}</span>
                    </span>
                    {profile.sessionMessages > 0 && <Badge count={profile.sessionMessages} color="#0e7c66" />}
                  </button>
                );
              }}
            />
          )}
        </div>
      </section>

      <main className="hac-chat">
        {targetPhone ? (
          <>
            <header className="hac-chat-header">
              <div className="hac-chat-title">
                <Avatar className="hac-contact-avatar">{getContactInitials(selectedProfile.name)}</Avatar>
                <div>
                  <Title level={4}>{selectedProfile.name}</Title>
                  <Text>{selectedProfile.displayPhone}</Text>
                </div>
              </div>
              <Space>
                <Select
                  value="support"
                  options={[
                    { value: 'support', label: 'Transfer to Support' },
                    { value: 'sales', label: 'Transfer to Sales' },
                    { value: 'billing', label: 'Transfer to Billing' },
                  ]}
                  className="hac-transfer"
                />
                <Button>Resolve</Button>
              </Space>
            </header>

            <div className="hac-message-canvas">
              <div className="hac-day-chip">Today</div>
              {loadingMessages ? (
                <div className="hac-loading"><Spin /></div>
              ) : messages.length === 0 ? (
                <div className="hac-empty-chat">
                  <MessageOutlined />
                  <Title level={4}>Seems clear</Title>
                  <Text>Select another contact or send a template to restart the conversation.</Text>
                </div>
              ) : (
                messages.map((item) => {
                  const bubbleType = getBubbleType(item);
                  return (
                    <div className={`hac-message-line ${bubbleType}`} key={item.id}>
                      <div className="hac-message-bubble">
                        {bubbleType === 'system' && <Tag color="default">System</Tag>}
                        <div>{item.text}</div>
                        <time>{item.time}</time>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <footer className="hac-composer">
              <Space className="hac-composer-tools">
                <Tooltip title="Canned templates"><Button type="text" icon={<ThunderboltOutlined />} /></Tooltip>
                <Tooltip title="Attach file"><Button type="text" icon={<PaperClipOutlined />} /></Tooltip>
                <Tooltip title="Contact context"><Button type="text" icon={<InfoCircleOutlined />} /></Tooltip>
              </Space>
              <TextArea
                autoSize={{ minRows: 1, maxRows: 5 }}
                value={inputText}
                onChange={(event) => setInputText(event.target.value)}
                onPressEnter={(event) => {
                  if (!event.shiftKey) {
                    event.preventDefault();
                    handleSendHumanMessage();
                  }
                }}
                placeholder='Type "/" for canned messages'
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleSendHumanMessage}
              >
                Send
              </Button>
            </footer>
          </>
        ) : (
          <div className="hac-empty-state">
            <UserSwitchOutlined />
            <Title level={3}>Human Agent Console</Title>
            <Text>Select a WhatsApp contact to review context and reply as a human agent.</Text>
          </div>
        )}
      </main>

      {targetPhone && (
        <ContactProfilePanel profile={selectedProfile} timer={timer} isConnected={isConnected} />
      )}
    </div>
  );
};

export default HumanAgentTerminal;
